import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getTradingOrders } from "../api/endpoints/trading/trading";
import type { Order } from "../api/models/order";
import { getBearerToken } from "../lib/authClient";

import "./OrdersPage.css";

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

const STATUS_LABELS: Record<string, string> = {
  NEW: "Aberta",
  PARTIALLY_FILLED: "Parcial",
  FILLED: "Executada",
  CANCELLED: "Cancelada",
  REJECTED: "Rejeitada",
};

const ORDER_TYPE_LABELS: Record<string, string> = {
  MARKET: "Mercado",
  LIMIT: "Limite",
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getTradingOrders(undefined, authHeaders());
        if (cancelled) return;
        if (response.status === 200 && "data" in response.data) {
          setOrders(response.data.data);
        } else {
          const msg = (response.data as { message?: string })?.message ??
            `Falha ao listar ordens (status ${response.status}).`;
          setError(msg);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Erro ao listar ordens:", err);
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOrders();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page-centered">
      <div className="orders-wrap">
        <button
          type="button"
          className="orders-back"
          onClick={() => navigate(-1)}
        >
          ← Voltar
        </button>

        <h1 className="orders-title">Minhas ordens</h1>

        {loading && <div className="orders-status">Carregando ordens...</div>}

        {error && !loading && (
          <div className="orders-status orders-status--error">{error}</div>
        )}

        {!loading && !error && orders.length === 0 && (
          <div className="orders-status">Você ainda não tem ordens.</div>
        )}

        {!loading && !error && orders.length > 0 && (
          <div className="orders-list">
            {orders.map((order) => (
              <button
                key={order.internalId}
                type="button"
                className="orders-item"
                onClick={() => navigate(`/wallet/history/${order.historyId}`)}
              >
                <div className="orders-item-top">
                  <div className="orders-item-pair">
                    <span className="orders-item-symbol">
                      {order.baseCoin}/{order.quoteCoin}
                    </span>
                    <span
                      className={`orders-sidePill orders-sidePill--${order.side.toLowerCase()}`}
                    >
                      {order.side === "BUY" ? "Compra" : "Venda"}
                    </span>
                  </div>
                  <span
                    className={`orders-statusPill orders-statusPill--${order.orderStatus.toLowerCase()}`}
                  >
                    {STATUS_LABELS[order.orderStatus] ?? order.orderStatus}
                  </span>
                </div>
                <div className="orders-item-bottom">
                  <span className="orders-item-meta">
                    {ORDER_TYPE_LABELS[order.orderType] ?? order.orderType}
                    {" · "}
                    {order.qty} {order.baseCoin}
                    {order.price ? ` · ${order.price} ${order.quoteCoin}` : ""}
                  </span>
                  <span className="orders-item-date">
                    {formatDateTime(order.createdAt)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
